import { useEffect, useState } from "react";
import { Box, Stack, Typography, Alert, Snackbar } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import CompanyGeneralForm from "./components/CompanyGeneralForm";
import { PaymentSubscriptionSection } from "../Payment/components/PaymentSubscriptionSection";
import { PaymentBillingTab } from "../Payment/components/PaymentBillingTab";

import { useGetOneOrganization } from "src/api/client-api/company/useGetOneOrganization.ts";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { useCurrentSubscription } from "src/api/client-api/payment/useCurrentSubscription";

export const ProfileSettings = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [showSuccess, setShowSuccess] = useState(false);
    const { selectedCompany } = useFoodpilotContext();
    const { isFeatureActivated } = useApplicationParameters();
    const isPaymentEnabled = isFeatureActivated("FS-payment-enabled");
    const companyId = selectedCompany?.id?.toString();
    const { company, isLoading } = useGetOneOrganization({ companyId, withoutRelations: true });
    const { data: subscription } = useCurrentSubscription({ isPaymentFeatureEnabled: isPaymentEnabled });

    const requiredMissing = !(
        (company?.name ?? "").trim() &&
        (company?.email ?? "").trim() &&
        (company?.country ?? "").trim() &&
        (company?.zipcode ?? "").trim()
    );

    useEffect(() => {
        // Check for Stripe's default session_id parameter OR our custom payment=success
        const sessionId = searchParams.get("session_id");
        const paymentSuccess = searchParams.get("payment") === "success";

        if (sessionId || paymentSuccess) {
            setShowSuccess(true);

            // Clean up URL parameters
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("session_id");
            newParams.delete("payment");

            // Replace URL without parameters
            const newUrl =
                newParams.toString() ? `${window.location.pathname}?${newParams.toString()}` : window.location.pathname;

            window.history.replaceState({}, "", newUrl);
        }
    }, [searchParams]);

    return (
        <>
            <Box sx={{ p: theme.spacing(6), mx: "auto" }}>
                <Stack gap={theme.spacing(6)}>
                    {/* General Information Section */}
                    <Stack gap={theme.spacing(3)}>
                        <Typography variant="h2" sx={{ fontSize: "24px", fontWeight: 700 }}>
                            {t("companySettings.general.title", "General")}
                        </Typography>
                        {isPaymentEnabled && requiredMissing && !isLoading && (
                            <Alert severity="info">
                                {t(
                                    "companySettings.general.required.info",
                                    "Please complete required information: Company name, Email, Country, Zip code.",
                                )}
                            </Alert>
                        )}

                        <CompanyGeneralForm />
                    </Stack>

                    {/* Subscription & Payment Section */}
                    {isPaymentEnabled && (
                        <Stack gap={theme.spacing(3)}>
                            <Typography variant="h2" sx={{ fontSize: "24px", fontWeight: 700 }}>
                                {t("companySettings.subscription.title", "Subscription & payment")}
                            </Typography>

                            <PaymentSubscriptionSection />

                            {/* Billing Widget - Only show if subscription is active */}
                            {subscription && <PaymentBillingTab />}
                        </Stack>
                    )}
                </Stack>
            </Box>

            {/* Success notification */}
            <Snackbar
                open={showSuccess}
                autoHideDuration={6000}
                onClose={() => setShowSuccess(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: "100%" }}>
                    {t("companySettings.payment.success", "Payment successful! Your subscription is now active.")}
                </Alert>
            </Snackbar>
        </>
    );
};
